import { Button, cn, ToggleGroup, ToggleGroupItem } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubCatalogType } from "@models";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  HUB_CATALOG_TYPE_FILTER_ALL,
  mapCatalogTypesToFilterItems,
  resolveHubCatalogTypeFilterSelection,
  shouldShowHubCatalogTypeFilterAll,
} from "./hubManagerCatalogFilters";

interface IHubManagerCatalogTypeFilterProps {
  catalogTypes: IHubCatalogType[];
  selectedTypeIds: string[];
  onSelectedTypeIdsChange: (typeIds: string[]) => void;
}

const SCROLL_STEP_PX = 120;

const hideHorizontalScrollbarClassName =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

export function HubManagerCatalogTypeFilter({
  catalogTypes,
  selectedTypeIds,
  onSelectedTypeIdsChange,
}: IHubManagerCatalogTypeFilterProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const filterItems = useMemo(
    () => mapCatalogTypesToFilterItems(catalogTypes),
    [catalogTypes],
  );

  const showAllOption = shouldShowHubCatalogTypeFilterAll(filterItems.length);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const overflow = scrollWidth > clientWidth + 1;

    setHasOverflow(overflow);
    setCanScrollLeft(overflow && scrollLeft > 0);
    setCanScrollRight(overflow && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    updateScrollState();

    element.addEventListener("scroll", updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);
    resizeObserver.observe(element.firstElementChild ?? element);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [filterItems, showAllOption, updateScrollState]);

  const scrollByStep = useCallback((direction: "left" | "right") => {
    const element = scrollRef.current;
    if (!element) return;

    const delta = direction === "left" ? -SCROLL_STEP_PX : SCROLL_STEP_PX;
    element.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const handleValueChange = useCallback(
    (nextTypeIds: string[]) => {
      onSelectedTypeIdsChange(
        resolveHubCatalogTypeFilterSelection(selectedTypeIds, nextTypeIds),
      );
    },
    [onSelectedTypeIdsChange, selectedTypeIds],
  );

  if (filterItems.length === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {hasOverflow ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="size-8 shrink-0 p-0"
          disabled={!canScrollLeft}
          aria-label={t("hub.catalog_manager.filter_scroll_left", {
            defaultValue: "Scroll catalog types left",
          })}
          onClick={() => scrollByStep("left")}>
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
      ) : null}

      <div
        ref={scrollRef}
        className={cn(
          "min-w-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch]",
          hasOverflow && hideHorizontalScrollbarClassName,
        )}>
        <ToggleGroup
          type="multiple"
          groupVariant="withSpacing"
          size="sm"
          variant="outline"
          value={selectedTypeIds}
          onValueChange={handleValueChange}
          className="flex w-max flex-nowrap">
          {showAllOption ? (
            <ToggleGroupItem
              value={HUB_CATALOG_TYPE_FILTER_ALL}
              className="mr-2 shrink-0">
              {t("hub.home.filter_all", { defaultValue: "All" })}
            </ToggleGroupItem>
          ) : null}
          {filterItems.map((item) => (
            <ToggleGroupItem
              key={item.id}
              value={item.id}
              className="mr-2 shrink-0">
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {hasOverflow ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="size-8 shrink-0 p-0"
          disabled={!canScrollRight}
          aria-label={t("hub.catalog_manager.filter_scroll_right", {
            defaultValue: "Scroll catalog types right",
          })}
          onClick={() => scrollByStep("right")}>
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
